import { html } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { RaygunElement } from '@src/js/common/rg-element.js'
import { waitForEvent } from '@src/js/common/wait-for.js'

@customElement('rg-modal')
export class RgTransition extends RaygunElement {

  @property({ type: Boolean, reflect: true })
  open = false

  @property({ type: Boolean, reflect: true })
  active = false

  @property({ type: String, attribute: 'bg-color' })
  bgColor = '#000'

  @property({ type: Number, attribute: 'bg-opacity' })
  bgOpacity = 0.5

  @property({ type: Number, attribute: 'transition-duration' })
  tntDuration = 0.35

  @property({ type: Boolean, attribute: 'click-to-close' })
  clickToClose = false

  @property({ type: Boolean, attribute: 'lock-scroll' })
  lockScroll = false

  @property({ type: Boolean, attribute: 'esc-to-close' })
  escToClose = false

  @property({ type: Boolean, attribute: 'focus-trap' })
  focusTrap = false

  @property({ type: String, attribute: 'focus-target' })
  focusTarget = ''

  @state({ type: Boolean })
  useScopeAttribute = true

  registerEvents() {
    return {
      'document.keydown:esc': this.onEscDown,
      'document.keydown:tab': this.onTabKey,
      'document.keyup:tab': this.onTabKey,
    }
  }

  onTabKey = (event) => {
    if (!this.open) return
    if (document.activeElement.closest('rg-modal') !== this) {
      if (this.focusTrap) {
        this.refs.section.focus()
        event.preventDefault()
      } else {
        this.toggleAttribute('open', false)
      }
    }
  }

  onBackdropClick = (event) => {
    const backdrop = event.target === this.refs.section;
    if (this.clickToClose && backdrop) {
      this.toggleAttribute('open', false)
    }
  }

  onEscDown = () => {
    if (this.escToClose) {
      this.toggleAttribute('open', false)
    }
  }

  async openModal() {
    this.notify('before-open')
    await this.toggleTransition(true)
    this.toggleFocus()
    this.notify('opened')
  }

  async closeModal() {
    this.notify('before-close')
    await this.toggleTransition(false)
    this.toggleFocus()
    this.notify('closed')
  }

  toggleFocus() {
    const { section } = this.refs
    if (section) {
      section.setAttribute('tabindex', this.open ? 0 : -1)
      if (this.focusTarget.length > 0) {
        const target = section.querySelector(this.focusTarget)
        if (target) {
          target.focus()
          return
        }
      }
      section.focus()
    }
  }

  async toggleTransition(visible) {
    const { transition } = this.refs
    if (transition) {
      this.toggleAttribute('active', true)
      const eventName = visible ? 'enter-end' : 'leave-end'
      transition.toggleAttribute('active', visible)
      await waitForEvent(transition, eventName)
      this.toggleAttribute('active', false)
    }
  }

  updated(changedProperties) {
    if (typeof changedProperties.get('open') === 'boolean') {
      this.open ? this.openModal() : this.closeModal()
    }
  }

  notify(eventName) {
    this.dispatchEvent(new Event(eventName, {
      bubbles: true,
      composed: true,
      cancelable: true,
    }))
  }

  getScrollLockCss() {
    return (this.lockScroll && this.open) ? 'html { overflow: hidden; }' : ''
  }

  render() {
    const scopedClass = `rg-modal[${this.scopeId}]`
    return html`
      <style>
        ${this.getScrollLockCss()}
        ${scopedClass} {
          display: none;
          position: fixed;
          z-index: var(--modal-z-index, 1000);
        }

        ${scopedClass}[active],
        ${scopedClass}[open],
        ${scopedClass} section,
        ${scopedClass} rg-transition,
        ${scopedClass} rg-transition::before {
          display: block;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
        }

        ${scopedClass} section {
          overflow-y: auto;
          overscroll-behavior: contain;
        }

        ${scopedClass} rg-transition::before {
          position: absolute;
          content: '';
          background: ${this.bgColor};
          opacity: ${this.bgOpacity};
          z-index: -1;
        }

        ${scopedClass} rg-transition {
          transition-property: opacity;
          transition-duration: ${this.tntDuration}s;
          transition-timing-function: ease-out;
          opacity: 0;
        }

        ${scopedClass} rg-transition.enter-active,
        ${scopedClass} rg-transition.enter-to {
          opacity: 1;
        }
      </style>
      <rg-transition
        ref="transition"
        @click="${this.onBackdropClick}"
      >
        <section
          ref="section"
          aria-modal="${this.open}"
          aria-hidden="${!this.open}"
          role="dialog"
        >
          ${this.slot()}
        </section>
      </rg-transition>
    `
  }
}
